import { memo, SVGProps } from 'react';
import type { CSSProperties, Ref } from 'react';
import { SVGCircle, SVGGroup, SVGGroupProps } from './styleableSvgComponents';
import { cn } from '@ifixit/ui/lib/utils';

type BasicHotspotCircleProps = {
   fill?: string;
   hoverFill?: string;
   focusFill?: string;
   className?: string;
} & Omit<SVGProps<SVGCircleElement>, 'fill' | 'cx' | 'cy' | 'r'>;

type BasicHotspotProps = {
   x: number;
   y: number;
   innerCircleProps?: BasicHotspotCircleProps;
   outerCircleProps?: BasicHotspotCircleProps;
   groupProps?: SVGGroupProps;
   ref?: Ref<SVGGElement>;
};

// Forwarded for tippy:
// https://github.com/atomiks/tippy.js-react#component-children
export const BasicHotspot = memo(function BasicHotspot({
   ref,
   x,
   y,
   innerCircleProps,
   outerCircleProps,
   groupProps,
}: BasicHotspotProps) {
   const {
      fill: outerFill,
      hoverFill: outerHoverFill,
      focusFill: outerFocusFill,
      className: outerClassName,
      ...outerRest
   } = outerCircleProps || {};
   const {
      fill: innerFill,
      hoverFill: innerHoverFill,
      focusFill: innerFocusFill,
      className: innerClassName,
      ...innerRest
   } = innerCircleProps || {};

   return (
      <SVGGroup
         ref={ref}
         {...groupProps}
         className={cn(
            'group cursor-pointer outline-none focus:outline-none',
            groupProps?.className
         )}
      >
         <SVGCircle
            {...outerRest}
            x={x}
            y={y}
            r={18}
            fill={outerFill}
            className={cn('transition-colors', outerClassName)}
            style={
               {
                  '--hover-fill': outerHoverFill || outerFill,
                  '--focus-fill': outerFocusFill || outerFill,
               } as CSSProperties
            }
         />
         <SVGCircle
            {...innerRest}
            x={x}
            y={y}
            r={6}
            fill={innerFill}
            className={cn('transition-colors', innerClassName)}
            style={
               {
                  '--hover-fill': innerHoverFill || innerFill,
                  '--focus-fill': innerFocusFill || innerFill,
               } as CSSProperties
            }
         />
         <style>{`
            g.group:hover circle:first-of-type,
            g.group:focus circle:first-of-type {
               fill: var(--hover-fill);
            }
            g.group:hover circle:last-of-type,
            g.group:focus circle:last-of-type {
               fill: var(--hover-fill);
            }
         `}</style>
      </SVGGroup>
   );
});
